import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { AiOutlineClose } from "react-icons/ai";
import { MdDeleteForever } from "react-icons/md";
import {
    useGetTdsCertificatesQuery,
    useUploadTdsCertificateMutation,
    useUpdateTdsCertificateMutation,
    useDeleteTdsCertificateMutation,
} from "../../../features/tenant/tdsCertificate/tdsCertificateApi";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";

export const TdsCertificateModal = ({ show, onClose, user }) => {
    const [file, setFile] = useState(null);
    const [financialYear, setFinancialYear] = useState("2025-26");
    const [quarter, setQuarter] = useState("Q1");
    const [isUploading, setIsUploading] = useState(false);

    const token = sessionStorage.getItem("access_token");
    const vendorData = JSON.parse(sessionStorage.getItem("vendorData") || "{}");
    const tenant_id = vendorData.tenant_id;

    const {
        data: certificatesData,
        isLoading: isFetching,
        refetch,
    } = useGetTdsCertificatesQuery(
        { token, tenant_id, app_user_id: user?.id },
        { skip: !show || !user?.id }
    );

    const [uploadSingleFile] = useUploadSingleFileMutation();
    const [uploadTdsCertificate, { isLoading: isSubmitting }] =
        useUploadTdsCertificateMutation();
    const [deleteTdsCertificate] = useDeleteTdsCertificateMutation();

    const certificatesList = certificatesData?.data || [];

    const handleFileUpload = async (e) => {
        e.preventDefault();
        if (!file || !financialYear || !quarter) {
            toast.error("Please fill all required fields");
            return;
        }

        try {
            setIsUploading(true);
            const formData = new FormData();
            formData.append("image", file);

            const uploadRes = await uploadSingleFile({
                token,
                body: formData,
            }).unwrap();

            if (uploadRes?.body?.fileLink) {
                const certificateUrl = uploadRes.body.fileLink;

                await uploadTdsCertificate({
                    token,
                    tenant_id,
                    body: {
                        app_user_id: user.id,
                        financial_year: financialYear,
                        quarter,
                        certificate_url: certificateUrl,
                    },
                }).unwrap();

                toast.success("TDS Certificate uploaded successfully!");
                setFile(null);
                refetch();
            } else {
                toast.error("File upload failed");
            }
        } catch (error) {
            console.error(error);
            if (error?.status === 409) {
                toast.error("TDS Certificate for this quarter and financial year already exists");
            } else {
                toast.error(error?.data?.message || "Something went wrong uploading TDS Certificate");
            }
        } finally {
            setIsUploading(false);
            // Reset the file input visually
            const fileInput = document.getElementById("tds_file_input");
            if (fileInput) fileInput.value = "";
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this certificate?")) return;
        try {
            await deleteTdsCertificate({ token, tenant_id, id }).unwrap();
            toast.success("TDS Certificate deleted successfully!");
            refetch();
        } catch (error) {
            console.error(error);
            toast.error(error?.data?.message || "Error deleting certificate");
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl p-6 md:p-8 relative max-h-[90vh] overflow-y-auto w-full animate-in zoom-in-95 duration-200 ring-1 ring-black/5">
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 rounded-full text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all focus:outline-none focus:ring-2 focus:ring-gray-900"
                    aria-label="Close modal"
                >
                    <AiOutlineClose size={20} />
                </button>

                <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1.5 tracking-tight">
                        TDS Certificates
                    </h2>
                    <p className="text-sm text-gray-500 font-medium tracking-wide">
                        {user?.name} <span className="mx-1.5 text-gray-300">•</span> {user?.mobile}
                    </p>
                </div>

                {/* Upload Form */}
                <form onSubmit={handleFileUpload} className="grid grid-cols-1 sm:grid-cols-[120px_100px_1fr_auto] gap-4 mb-10 pb-8 border-b border-gray-100 items-end">
                    <div className="w-full">
                        <label className="block text-[11px] font-bold text-gray-500 mb-2 uppercase tracking-wider">Year</label>
                        <select
                            value={financialYear}
                            onChange={(e) => setFinancialYear(e.target.value)}
                            className="w-full border border-gray-200 text-gray-900 text-sm py-2 px-3 rounded-lg hover:border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none transition-all cursor-pointer shadow-sm"
                        >
                            <option value="2025-26">2025-26</option>
                            <option value="2026-27">2026-27</option>
                        </select>
                    </div>
                    <div className="w-full">
                        <label className="block text-[11px] font-bold text-gray-500 mb-2 uppercase tracking-wider">Quarter</label>
                        <select
                            value={quarter}
                            onChange={(e) => setQuarter(e.target.value)}
                            className="w-full border border-gray-200 text-gray-900 text-sm py-2 px-3 rounded-lg hover:border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none transition-all cursor-pointer shadow-sm"
                        >
                            <option value="Q1">Q1</option>
                            <option value="Q2">Q2</option>
                            <option value="Q3">Q3</option>
                            <option value="Q4">Q4</option>
                        </select>
                    </div>
                    <div className="w-full">
                        <label className="block text-[11px] font-bold text-gray-500 mb-2 uppercase tracking-wider">File</label>
                        <input
                            id="tds_file_input"
                            type="file"
                            onChange={(e) => setFile(e.target.files[0])}
                            className="block w-full text-sm text-gray-500 border border-gray-200 rounded-lg cursor-pointer bg-white file:mr-3 file:py-2 flex-1 file:px-4 file:border-0 file:text-sm file:font-semibold file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100 hover:border-gray-300 focus:ring-2 focus:ring-gray-900 focus:outline-none transition-all shadow-sm h-[40px]"
                            accept=".pdf,image/*"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isUploading || isSubmitting || !file}
                        className="bg-gray-900 hover:bg-black text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all h-[40px] shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 active:scale-[0.98]"
                    >
                        {isUploading || isSubmitting ? "Uploading" : "Upload"}
                    </button>
                </form>

                {/* List of Certificates */}
                {isFetching ? (
                    <div className="flex justify-center items-center py-16">
                        <CircleLoaderComponent />
                    </div>
                ) : (
                    <div>
                        <table className="w-full text-sm text-left">
                            <thead>
                                <tr>
                                    <th className="pb-4 px-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 w-28">Year</th>
                                    <th className="pb-4 px-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 w-24">Quarter</th>
                                    <th className="pb-4 px-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">Date</th>
                                    <th className="pb-4 px-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">Document</th>
                                    <th className="pb-4 px-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 text-right w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {certificatesList?.length > 0 ? (
                                    certificatesList.map((cert) => (
                                        <tr key={cert.id} className="group hover:bg-gray-50 transition-colors rounded-xl">
                                            <td className="py-4 px-2 border-b border-gray-50 text-gray-900 font-medium group-last:border-0">{cert.financial_year}</td>
                                            <td className="py-4 px-2 border-b border-gray-50 text-gray-600 group-last:border-0">{cert.quarter}</td>
                                            <td className="py-4 px-2 border-b border-gray-50 text-gray-500 group-last:border-0">
                                                {cert.created_at ? new Date(cert.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                            </td>
                                            <td className="py-4 px-2 border-b border-gray-50 group-last:border-0">
                                                <a href={cert.certificate_url} target="_blank" rel="noreferrer" className="text-gray-900 hover:text-blue-600 font-medium hover:underline decoration-blue-200 underline-offset-4 outline-none focus:ring-2 focus:ring-blue-500 rounded-sm transition-colors cursor-pointer">
                                                    View
                                                </a>
                                            </td>
                                            <td className="py-4 px-2 border-b border-gray-50 text-right align-middle group-last:border-0">
                                                <button
                                                    onClick={() => handleDelete(cert.id)}
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 focus:ring-2 focus:ring-red-500 outline-none inline-flex items-center justify-center transform group-hover:scale-100 scale-95"
                                                    title="Delete certificate"
                                                    aria-label="Delete certificate"
                                                >
                                                    <MdDeleteForever size={20} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="py-16 text-center">
                                            <p className="text-gray-400 text-sm font-medium">No certificates found.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
